import type {
    ContentDefinition,
    DocumentBlock,
    ForDefinition,
    ForOutput,
    ImportCachedOutput,
    ImportDocumentSnapshot,
    ParsedDocument,
    PluginDefinition,
    PluginOutput,
    UseBinding,
} from "@magic-md/core";
import {
    applyTemplateInputRefsToValue,
    buildDefaultedTemplateInputs,
    extractTemplateParams,
    getBlockingTemplateParams,
    substituteTemplateInputRefs,
} from "@magic-md/core";
import { collectForScopedUnits } from "./for-scope-units";
import {
    normalizeForDefinition,
    normalizeForDefinitions,
} from "./for-definition-normalize";
import { getImportCachedSymbolOutput } from "./import-cache-output";
import { mergeHydratedExternalConfigDefinition } from "./external-config-definition";
import { getCachedContentInnerParse } from "./content-inner-parse-cache";
import { collectImportDocuments } from "./import-documents";
import { materializePluginUseDefinition } from "./use-materialization";
import type {
    ContentExecutionEntry,
    ExecutionEntry,
    ImportExecutionEntry,
    UseExecutionEntry,
} from "../store/plugin-execution-store";

function collectContentScopedUnits(parsedDocument: ParsedDocument | null | undefined) {
    const scopedDefinitions = new Map<string, { definition: PluginDefinition; ownerContentId: string }>();
    const scopedContentDefinitions = new Map<string, { contentDefinition: ContentDefinition; ownerContentId: string }>();
    const scopedUses: Array<{ use: UseBinding; ownerContentId: string }> = [];
    const seenUseIds = new Set<string>();
    const rootContentDefinitions =
        parsedDocument?.contentDefinitions ?? new Map<string, ContentDefinition>();
    const queue = Array.from(rootContentDefinitions, ([id, contentDefinition]) => ({
        id,
        contentDefinition,
    }));
    const seenContentIds = new Set<string>(rootContentDefinitions.keys());

    for (let i = 0; i < queue.length; i += 1) {
        const { id: ownerContentId, contentDefinition } = queue[i];
        try {
            const inner = getCachedContentInnerParse(contentDefinition.body);
            for (const [definitionId, definition] of inner.definitions) {
                if (!scopedDefinitions.has(definitionId)) {
                    scopedDefinitions.set(definitionId, { definition, ownerContentId });
                }
            }
            for (const [contentId, innerContentDefinition] of inner.contentDefinitions) {
                if (!rootContentDefinitions.has(contentId) && !scopedContentDefinitions.has(contentId)) {
                    scopedContentDefinitions.set(contentId, {
                        contentDefinition: innerContentDefinition,
                        ownerContentId,
                    });
                }
                if (!seenContentIds.has(contentId)) {
                    seenContentIds.add(contentId);
                    queue.push({ id: contentId, contentDefinition: innerContentDefinition });
                }
            }
            for (const use of inner.uses) {
                if (!seenUseIds.has(use.id)) {
                    seenUseIds.add(use.id);
                    scopedUses.push({ use, ownerContentId });
                }
            }
        } catch {
            // Ignore parse errors in content body.
        }
    }

    return {
        definitions: scopedDefinitions,
        contentDefinitions: scopedContentDefinitions,
        uses: scopedUses,
    };
}

function stripDefinitionOutput(definition: PluginDefinition): PluginDefinition {
    if (definition.output === undefined) return definition;
    const { output: _output, ...rest } = definition;
    return rest;
}

function isImportedNamespaceUnit(
    parsedDocument: ParsedDocument | null | undefined,
    id: string,
): boolean {
    return !!parsedDocument?.imports?.some((imp) => id.startsWith(`${imp.namespace}/`));
}

function putDefinitionOutput(
    outputs: Map<string, PluginOutput>,
    id: string,
    output: PluginDefinition["output"] | null | undefined,
): void {
    if (output != null) outputs.set(id, output);
}

function putImportedSymbols(
    namespace: string,
    cached: ImportCachedOutput,
    definitions: Map<string, PluginDefinition>,
    contentDefinitions: Map<string, ContentDefinition>,
    forDefinitions: Map<string, ForDefinition>,
    outputs: Map<string, PluginOutput>,
): void {
    for (const sym of [...(cached.symbols ?? []), ...(cached.instanceSymbols ?? [])]) {
        const cachedOutput = getImportCachedSymbolOutput(namespace, cached, sym);
        if (sym.kind === "node" && sym.pluginName && !definitions.has(sym.qualifiedId)) {
            const def: PluginDefinition = {
                id: sym.qualifiedId,
                pluginName: sym.pluginName,
                config: sym.config ?? {},
                template: sym.template ?? false,
                params: sym.params,
                inputSchema: sym.inputSchema,
            };
            definitions.set(sym.qualifiedId, def);
            putDefinitionOutput(outputs, sym.qualifiedId, cachedOutput as PluginDefinition["output"] | undefined);
            continue;
        }

        if (sym.kind === "content" && sym.body != null && !contentDefinitions.has(sym.qualifiedId)) {
            contentDefinitions.set(sym.qualifiedId, {
                id: sym.qualifiedId,
                kind: "content",
                template: sym.template ?? false,
                body: sym.body,
                params: sym.params,
                inputSchema: sym.inputSchema,
                range: { start: 0, end: 0 },
                ...(cachedOutput ? { output: cachedOutput as ContentDefinition["output"] } : {}),
            });
            continue;
        }

        if (sym.kind === "for" && sym.forDefinition && !forDefinitions.has(sym.qualifiedId)) {
            const forOutput = cachedOutput as ForOutput | undefined;
            forDefinitions.set(sym.qualifiedId, normalizeForDefinition({
                ...sym.forDefinition,
                id: sym.qualifiedId,
                template: sym.forDefinition.template ?? sym.template,
                params: sym.forDefinition.params ?? sym.params,
                inputSchema: sym.forDefinition.inputSchema ?? sym.inputSchema,
                ...(forOutput ? { output: forOutput } : {}),
            }));
        }
    }
}

function cloneForDefinitionForRender(
    source: ForDefinition,
    templateInputs: Record<string, unknown>,
): ForDefinition {
    const normalizedSource = normalizeForDefinition(source);
    const shouldSubstitute = Object.keys(templateInputs).length > 0;
    const apply = <T,>(value: T): T =>
        shouldSubstitute ? applyTemplateInputRefsToValue(value, templateInputs) as T : value;

    const definitions = new Map<string, PluginDefinition>();
    for (const [id, definition] of normalizedSource.innerScope.definitions) {
        definitions.set(id, {
            ...definition,
            config: apply(definition.config),
        });
    }

    const contentDefinitions = new Map<string, ContentDefinition>();
    for (const [id, contentDefinition] of normalizedSource.innerScope.contentDefinitions) {
        contentDefinitions.set(id, {
            ...contentDefinition,
            body: apply(contentDefinition.body),
        });
    }

    const forDefinitions = new Map<string, ForDefinition>();
    for (const [id, forDefinition] of normalizedSource.innerScope.forDefinitions) {
        forDefinitions.set(id, cloneForDefinitionForRender(forDefinition, templateInputs));
    }

    const rawOf = apply(normalizedSource.of);

    return {
        ...normalizedSource,
        of: typeof rawOf === "string" ? rawOf : JSON.stringify(rawOf),
        innerScope: {
            ...normalizedSource.innerScope,
            definitions,
            contentDefinitions,
            forDefinitions,
            uses: normalizedSource.innerScope.uses?.map((use) => ({
                ...use,
                with: use.with ? apply(use.with) : undefined,
                patch: use.patch ? apply(use.patch) : undefined,
                bind: use.bind ? { ...use.bind } : undefined,
            })),
            ...(normalizedSource.innerScope.blocks
                ? { blocks: apply(normalizedSource.innerScope.blocks) as DocumentBlock[] }
                : {}),
        },
    };
}

function materializeForUseDefinition(
    use: UseBinding,
    forDefinitions: Map<string, ForDefinition>,
): ForDefinition | undefined {
    const sourceFor = forDefinitions.get(use.source);
    if (!sourceFor) return undefined;

    const templateInputs = buildDefaultedTemplateInputs(use.with, sourceFor.inputSchema);
    const materialized = cloneForDefinitionForRender(sourceFor, templateInputs);
    const params = extractTemplateParams({
        of: materialized.of,
        blocks: materialized.innerScope.blocks,
    });
    const blockingParams = getBlockingTemplateParams(
        sourceFor.inputSchema,
        sourceFor.params,
        params,
    );
    const remainsTemplate = use.template === true || blockingParams.length > 0;

    return {
        ...materialized,
        id: use.id,
        template: remainsTemplate,
        params: params.length > 0 ? params : undefined,
        ...(remainsTemplate && use.inputSchema ? { inputSchema: use.inputSchema } : {}),
        ...(use.outputMode ? { outputMode: use.outputMode } : {}),
        ...(use.externalRef ? { externalRef: use.externalRef } : {}),
        ...(use.output?.forOutput ? { output: use.output.forOutput } : {}),
    };
}

function registerForInnerDefinitions(
    forDefinition: ForDefinition,
    definitions: Map<string, PluginDefinition>,
): void {
    for (const innerDef of forDefinition.innerScope.definitions.values()) {
        if (!definitions.has(innerDef.id)) {
            definitions.set(innerDef.id, innerDef);
        }
    }
}

export interface BuildDocumentRenderContextOptions {
    parsedDocument: ParsedDocument | null | undefined;
    executions?: Map<string, ExecutionEntry>;
    contentExecutions?: Map<string, ContentExecutionEntry>;
    importExecutions?: Map<string, ImportExecutionEntry>;
    useExecutions?: Map<string, UseExecutionEntry>;
    engineDefinitions?: Map<string, PluginDefinition>;
    engineContentDefinitions?: Map<string, ContentDefinition>;
}

export interface DocumentRenderContext {
    definitions: Map<string, PluginDefinition>;
    forDefinitions: Map<string, ForDefinition>;
    contentDefinitions: Map<string, ContentDefinition>;
    importDocuments: Map<string, ImportDocumentSnapshot>;
    outputs: Map<string, PluginOutput>;
}

export function buildDocumentRenderContext({
    parsedDocument,
    executions = new Map(),
    contentExecutions = new Map(),
    importExecutions = new Map(),
    useExecutions = new Map(),
    engineDefinitions = new Map(),
    engineContentDefinitions = new Map(),
}: BuildDocumentRenderContextOptions): DocumentRenderContext {
    const normalizedForDefinitions = normalizeForDefinitions(parsedDocument?.forDefinitions);
    const renderDocument = parsedDocument
        ? { ...parsedDocument, forDefinitions: normalizedForDefinitions }
        : parsedDocument;
    const contentScopedUnits = collectContentScopedUnits(renderDocument);
    const forScopedUnits = collectForScopedUnits(normalizedForDefinitions);

    const definitions = new Map<string, PluginDefinition>();
    const outputs = new Map<string, PluginOutput>();
    const forDefinitions = new Map<string, ForDefinition>(normalizedForDefinitions);
    const contentDefinitions = new Map<string, ContentDefinition>(
        renderDocument?.contentDefinitions ?? new Map(),
    );
    for (const [id, def] of renderDocument?.definitions ?? new Map<string, PluginDefinition>()) {
        definitions.set(id, stripDefinitionOutput(def));
        putDefinitionOutput(outputs, id, def.output);
    }

    if (renderDocument?.imports) {
        for (const imp of renderDocument.imports) {
            const cached = imp.output;
            if (!cached) continue;
            putImportedSymbols(
                imp.namespace,
                cached,
                definitions,
                contentDefinitions,
                forDefinitions,
                outputs,
            );
        }
    }

    for (const [id, def] of engineDefinitions) {
        const localDefinition = renderDocument?.definitions?.get(id);
        const hydratedExternal = localDefinition
            ? mergeHydratedExternalConfigDefinition(localDefinition, def)
            : null;
        if (hydratedExternal) {
            definitions.set(id, hydratedExternal);
            putDefinitionOutput(outputs, id, def.output);
            continue;
        }
        const isLocalDefinition = !!localDefinition;
        const shouldSet =
            !definitions.has(id) ||
            (!isLocalDefinition && isImportedNamespaceUnit(renderDocument, id));
        if (shouldSet) {
            definitions.set(id, stripDefinitionOutput(def));
            putDefinitionOutput(outputs, id, def.output);
        }
    }

    for (const forDef of forDefinitions.values()) {
        registerForInnerDefinitions(forDef, definitions);
    }
    for (const [id, scoped] of forScopedUnits.definitions) {
        if (!definitions.has(id)) {
            definitions.set(id, scoped.definition);
        }
    }
    for (const [id, scoped] of contentScopedUnits.definitions) {
        if (!definitions.has(id)) {
            const contentOutput = contentExecutions.get(scoped.ownerContentId)?.output;
            const output =
                (contentOutput?.[id] as PluginDefinition["output"] | undefined) ??
                scoped.definition.output;
            definitions.set(id, stripDefinitionOutput(scoped.definition));
            putDefinitionOutput(outputs, id, output);
        }
    }
    for (const [id, exec] of executions) {
        const execOutput = exec.output;
        if (execOutput == null) continue;
        putDefinitionOutput(outputs, id, execOutput);
    }

    const allUses = [
        ...(renderDocument?.uses ?? []),
        ...forScopedUnits.uses.map((entry) => entry.use),
        ...contentScopedUnits.uses.map((entry) => entry.use),
    ];
    if (allUses.length > 0) {
        for (const use of allUses) {
            const isDeclaredDefinition =
                (renderDocument?.definitions?.has(use.id) ?? false) ||
                forScopedUnits.definitions.has(use.id) ||
                contentScopedUnits.definitions.has(use.id);
            if (isDeclaredDefinition) continue;

            const materialized = materializePluginUseDefinition(use, definitions, allUses);
            if (!materialized) continue;

            const scopedUse = contentScopedUnits.uses.find((entry) => entry.use.id === use.id);
            const contentOutput = scopedUse
                ? contentExecutions.get(scopedUse.ownerContentId)?.output
                : undefined;
            const useExec = executions.get(use.id);
            const useOutput =
                useExec?.output ??
                use.output?.nodeOutput ??
                (contentOutput?.[use.id] as PluginDefinition["output"] | undefined) ??
                undefined;
            definitions.set(use.id, stripDefinitionOutput(materialized));
            putDefinitionOutput(outputs, use.id, useOutput);
        }
    }

    for (const [id, scoped] of contentScopedUnits.contentDefinitions) {
        if (!contentDefinitions.has(id)) {
            const execOutput = contentExecutions.get(id)?.output;
            const ownerOutput = contentExecutions.get(scoped.ownerContentId)?.output;
            const output =
                execOutput ??
                (ownerOutput?.[id] as ContentDefinition["output"] | undefined) ??
                scoped.contentDefinition.output;
            contentDefinitions.set(
                id,
                output ? { ...scoped.contentDefinition, output } : scoped.contentDefinition,
            );
        }
    }
    for (const [id, scoped] of forScopedUnits.contentDefinitions) {
        if (!contentDefinitions.has(id)) {
            contentDefinitions.set(id, scoped.contentDefinition);
        }
    }

    for (const [id, contentDef] of engineContentDefinitions) {
        const isLocalContent = renderDocument?.contentDefinitions?.has(id) ?? false;
        if (
            !contentDefinitions.has(id) ||
            (!isLocalContent && isImportedNamespaceUnit(renderDocument, id))
        ) {
            contentDefinitions.set(id, contentDef);
        }
    }
    for (const [id, exec] of contentExecutions) {
        if (exec.output === undefined) continue;
        const contentDef = contentDefinitions.get(id);
        if (!contentDef || contentDef.output === exec.output) continue;
        contentDefinitions.set(id, { ...contentDef, output: exec.output });
    }

    if (allUses.length > 0) {
        for (const use of allUses) {
            if (!definitions.has(use.id) && !contentDefinitions.has(use.id) && !forDefinitions.has(use.id)) {
                const materializedFor = materializeForUseDefinition(use, forDefinitions);
                if (materializedFor) {
                    forDefinitions.set(use.id, materializedFor);
                    registerForInnerDefinitions(materializedFor, definitions);
                }
            }

            const isDeclaredContent =
                (renderDocument?.contentDefinitions?.has(use.id) ?? false) ||
                contentScopedUnits.contentDefinitions.has(use.id) ||
                forScopedUnits.contentDefinitions.has(use.id);
            if (isDeclaredContent) continue;
            if (definitions.has(use.id)) continue;
            const contentSource = contentDefinitions.get(use.source);
            const scopedUse = contentScopedUnits.uses.find((entry) => entry.use.id === use.id);
            const existingContent = contentDefinitions.get(use.id);
            const useExec = useExecutions.get(use.id);
            const materializedBody = use.output?.materializedBody ?? useExec?.materializedBody;
            const cachedContentOutput = (
                use.output?.contentOutput ??
                contentExecutions.get(use.id)?.output ??
                (scopedUse
                    ? contentExecutions.get(scopedUse.ownerContentId)?.output?.[use.id]
                    : undefined)
            ) as ContentDefinition["output"] | undefined;

            if (contentSource) {
                const expandedBody = contentSource.template
                    ? substituteTemplateInputRefs(
                        contentSource.body,
                        buildDefaultedTemplateInputs(use.with, contentSource.inputSchema),
                    )
                    : contentSource.body;
                const params = extractTemplateParams(expandedBody);
                const blockingParams = getBlockingTemplateParams(
                    contentSource.inputSchema,
                    contentSource.params,
                    params,
                );
                const remainsTemplate = use.template === true || blockingParams.length > 0;
                contentDefinitions.set(use.id, {
                    ...contentSource,
                    id: use.id,
                    template: remainsTemplate,
                    params: params.length > 0 ? params : undefined,
                    body: expandedBody,
                    ...(use.outputMode ? { outputMode: use.outputMode } : {}),
                    ...(use.externalRef ? { externalRef: use.externalRef } : {}),
                    ...(cachedContentOutput ? { output: cachedContentOutput } : {}),
                });
            } else if (existingContent) {
                continue;
            } else if (materializedBody && useExec?.kind === "content") {
                contentDefinitions.set(use.id, {
                    id: use.id,
                    kind: "content",
                    template: false,
                    body: materializedBody,
                    range: use.range ?? { start: 0, end: 0 },
                    ...(use.outputMode ? { outputMode: use.outputMode } : {}),
                    ...(use.externalRef ? { externalRef: use.externalRef } : {}),
                    ...(cachedContentOutput ? { output: cachedContentOutput } : {}),
                });
            }
        }
    }

    const importDocuments = collectImportDocuments(
        renderDocument?.imports,
        importExecutions,
    );

    for (const [id, execution] of executions) {
        const output = execution.output;
        if (output != null) outputs.set(id, output);
    }

    return {
        definitions,
        forDefinitions,
        contentDefinitions,
        importDocuments,
        outputs,
    };
}
